import { useState, useEffect } from 'react';
import api from '../../utils/api';
import ReactMarkdown from 'react-markdown';
import { decode } from 'html-entities';
import { useLoading } from '../../context/LoadingContext';
import { useError } from '../../context/ErrorContext';
import CloseButton from '../modal/CloseButton';

interface GoogleTermsAcceptanceProps {
  onAccept: () => void;
}

const GoogleTermsAcceptance: React.FC<GoogleTermsAcceptanceProps> = ({ onAccept }) => {
  const [contractData, setContractData] = useState<any>(null);
  const [showContractModal, setShowContractModal] = useState<boolean>(false);
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const { setLoading } = useLoading();
  const { setError } = useError();

  const openContractModal = () => setShowContractModal(true);
  const closeContractModal = () => setShowContractModal(false);

  useEffect(() => {
    setLoading(true);
    api
      .get('/contract/cgc/family/ita', { skipAuth: true })
      .then((response) => {
        setContractData(response.data);
      })
      .catch((error) => {
        console.error('Errore nel caricamento del contratto:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setLoading]);

  useEffect(() => {
    if (showContractModal) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [showContractModal]);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeContractModal();
      }
    };

    if (showContractModal) {
      window.addEventListener('keydown', handleEsc);
    } else {
      window.removeEventListener('keydown', handleEsc);
    }

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [showContractModal]);

  const fixMalformedEntities = (str: string): string => {
    return str
      .replace(/&ldquo/g, '&ldquo;')
      .replace(/&rdquo/g, '&rdquo;')
      .replace(/&ldquopiattaforma/g, '&ldquo;piattaforma&rdquo;');
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.target.checked);
  };

  const handleSubmit = async () => {
    if (!isChecked) return;

    setLoading(true);
    try {
      await api.post('/auth/accept-terms', {
        terms_and_conditions: 1,
      });
      onAccept();
    } catch (error: any) {
      setError(
        true,
        error.response?.data?.message || 'Errore nell\'accettazione dei termini',
        error.response?.status || 500,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="bg-gradient-to-r from-fpcred to-buttonhover p-4 sm:p-6 lg:p-8">
        <div className="text-center text-white">
          <h1 className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold mb-1 sm:mb-2">
            Accetta le condizioni d'uso
          </h1>
          <p className="text-xs sm:text-sm lg:text-base opacity-90">
            Prima di continuare, leggi e accetta i nostri termini di servizio
          </p>
        </div>
      </div>

      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-md mx-auto space-y-6">
          <div className="flex flex-col">
            <button
              type="button"
              className="mb-4 px-4 py-2 bg-fpcpink text-fpcred hover:bg-fpcred hover:text-white font-medium rounded-lg transition-all duration-200 border-2 border-fpcred"
              onClick={openContractModal}>
              📄 Leggi le condizioni d'uso
            </button>
          </div>

          <div className="flex items-center gap-3">
            <input
              id="terms-checkbox"
              type="checkbox"
              checked={isChecked}
              onChange={handleCheckboxChange}
              className="w-5 h-5 text-fpcred rounded focus:ring-2 focus:ring-fpcred cursor-pointer"
            />
            <label htmlFor="terms-checkbox" className="text-sm sm:text-base cursor-pointer">
              Accetto le condizioni d'uso
            </label>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="flex justify-center">
            <button
              onClick={handleSubmit}
              disabled={!isChecked}
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-fpcred text-white text-base sm:text-lg font-semibold rounded-xl hover:bg-buttonhover disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 shadow-lg disabled:shadow-none disabled:transform-none">
              Continua
            </button>
          </div>
        </div>
      </div>

      {/* Modal del contratto */}
      {showContractModal && contractData && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50"
          onClick={closeContractModal}>
          <div
            className="bg-white p-6 rounded-md w-11/12 max-w-4xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end mb-4">
              <CloseButton onClick={closeContractModal} />
            </div>

            <h1 className="text-2xl font-bold mb-2">{contractData.title}</h1>

            <div className="mb-4">
              <h3 className="font-semibold">Accettazione</h3>
              {contractData && contractData.acceptance && (
                <ReactMarkdown>{decode(fixMalformedEntities(contractData.acceptance))}</ReactMarkdown>
              )}
            </div>

            {contractData.sections?.map((section: any, idx: number) => {
              const sectionNumber = idx + 1;
              return (
                <div key={idx} className="mb-4">
                  <h3 className="font-bold mb-1">
                    {sectionNumber}. {section.title}
                  </h3>
                  {section.content && <ReactMarkdown>{decode(fixMalformedEntities(section.content))}</ReactMarkdown>}

                  {section.subsections?.map((sub: any, sIdx: number) => {
                    const subSectionNumber = `${sectionNumber}.${sIdx + 1}`;
                    return (
                      <div key={sIdx} className="ml-4 mt-2 flex gap-2 border-l pl-4 border-gray-300">
                        {sub.title ? (
                          <h4 className="font-semibold">
                            {subSectionNumber}. {sub.title}
                          </h4>
                        ) : (
                          <p className="font-semibold">{subSectionNumber}.</p>
                        )}
                        {sub.content && <ReactMarkdown>{decode(fixMalformedEntities(sub.content))}</ReactMarkdown>}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            <div className="flex justify-center mt-6">
              <button
                type="button"
                onClick={closeContractModal}
                className="bg-fpcred text-white font-bold py-2 px-6 rounded-md hover:bg-buttonhover">
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleTermsAcceptance;
